import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { initialsOf } from '../Shared/cellHelpers';
import { StatusChip, type CellTone } from '../Shared/cells';
import { CopyableId } from '@/pages/Console/Audit/CopyableId';
import { isPendingFirstLogin, type KeycloakAccountRow } from '../types';
import type { AccountDetail } from '@/api/accounts';
import styles from './accountProfile.module.css';

interface Props {
    detail: AccountDetail;
    displayName: string;
    onReset: () => void;
    onLock: () => void;
    onDelete: () => void;
    canReset: boolean;
    canLock: boolean;
    canDelete: boolean;
    lockVisible: boolean;
}

/**
 * EAA-1820 (frame 72): profile hero — avatar, display name, status, username ·
 * email, sub, and the Reset / Lock / Delete actions from the handoff header.
 *
 * EAA-1804: actions swapped to the kit `Button` instead of hand-written
 * `<button className="portal-btn ...">`.
 */
export function AccountProfileHeader({
    detail,
    displayName,
    onReset,
    onLock,
    onDelete,
    canReset,
    canLock,
    canDelete,
    lockVisible,
}: Props) {
    const { t } = useTranslation();

    const tone: CellTone = !detail.enabled ? 'off' : isPendingFirstLogin(detail as KeycloakAccountRow) ? 'warn' : 'ok';
    const statusLabel = !detail.enabled
        ? t('console.accounts.table.statusLocked')
        : isPendingFirstLogin(detail as KeycloakAccountRow)
          ? t('console.accounts.table.statusPendingFirstLogin')
          : t('console.accounts.table.statusActive');

    return (
        <div className={styles.header}>
            <span className={styles.headerAvatar} aria-hidden="true">
                {initialsOf(displayName)}
            </span>
            <div className={styles.headerMain}>
                <span className={styles.headerTitleRow}>
                    <h1 className={styles.headerName}>{displayName}</h1>
                    <StatusChip tone={tone} label={statusLabel} />
                </span>
                <span className={styles.headerMeta}>
                    <code>{detail.username}</code>
                    {detail.email && (
                        <>
                            <span className={styles.headerSep}>·</span>
                            <span>{detail.email}</span>
                        </>
                    )}
                </span>
                <span className="acc-profile-header__sub">
                    <CopyableId value={detail.sub} />
                </span>
            </div>
            <div className={styles.headerActions}>
                {canReset && (
                    <Button onClick={onReset}>
                        {t('console.accounts.detailDrawer.resetButton')}
                    </Button>
                )}
                {canLock && lockVisible && (
                    <Button onClick={onLock}>
                        {t('console.accounts.detailDrawer.lockButton')}
                    </Button>
                )}
                {canDelete && (
                    <Button danger onClick={onDelete}>
                        {t('console.accounts.detailDrawer.deleteButton')}
                    </Button>
                )}
            </div>
        </div>
    );
}

export default AccountProfileHeader;
